import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, ArrowRightLeft, Store as StoreIcon, Pencil, Trash2 } from "lucide-react";
import DeleteDialog from "../components/DeleteDialog";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { WEEKDAYS } from "../lib/format";
import { PageHeader, Loading, Empty } from "../components/kit";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ROLE_LABEL = { admin: "Admin", manager: "Store Manager", staff: "Sales Staff" };
const STATUS_LABEL = { active: "Active", inactive: "Inactive", transferred: "Transferred", left: "Left BlueStone" };
const STATUS_CLS = { active: "bg-green-50 text-green-700 border-green-200", inactive: "bg-slate-100 text-slate-600 border-slate-200",
  transferred: "bg-sky-50 text-sky-700 border-sky-200", left: "bg-red-50 text-red-700 border-red-200" };

function StoreSelect({ value, onChange, stores, testid }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="h-11" data-testid={testid}><SelectValue placeholder="Select store" /></SelectTrigger>
      <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ProfileFields({ f, setF, prefix }) {
  return (
    <>
      <div className="space-y-1.5"><Label>BlueStone official email</Label><Input type="email" className="h-11" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@bluestone.com" data-testid={`${prefix}-email-input`} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Mobile number</Label><Input type="tel" inputMode="numeric" maxLength={10} className="h-11 num" value={f.mobile || ""} onChange={(e) => setF({ ...f, mobile: e.target.value.replace(/\D/g, "") })} data-testid={`${prefix}-mobile-input`} /></div>
        <div className="space-y-1.5"><Label>Date of joining</Label><Input type="date" className="h-11" value={f.date_of_joining || ""} onChange={(e) => setF({ ...f, date_of_joining: e.target.value })} data-testid={`${prefix}-doj-input`} /></div>
      </div>
    </>
  );
}

function CreateUser({ open, setOpen, onDone }) {
  const { user, stores, storeId } = useAuth();
  const blank = { name: "", employee_id: "", role: "staff", password: "", store_id: storeId !== "all" ? storeId : "", store_ids: [], weekly_off: "none", email: "", mobile: "", date_of_joining: "" };
  const [f, setF] = useState(blank);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setF(blank); setErr(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setErr("");
    try {
      await api.post("/users", { ...f, weekly_off: f.weekly_off === "none" ? null : Number(f.weekly_off), store_id: f.store_id || null });
      toast.success(`${f.name} created. Share Employee ID ${f.employee_id} and the temporary password.`);
      setOpen(false);
      onDone();
    } catch (e) { setErr(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#011E38]">New account</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Employee name</Label><Input className="h-11" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name-input" /></div>
            <div className="space-y-1.5"><Label>Employee ID</Label><Input className="h-11" value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="new-user-employee-id-input" /></div>
          </div>
          <ProfileFields f={f} setF={setF} prefix="new-user" />
          <div className="space-y-1.5"><Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })} disabled={user.role !== "admin"}>
              <SelectTrigger className="h-11" data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(user.role === "admin" ? ["staff", "manager", "admin"] : ["staff"]).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {f.role === "staff" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Store</Label><StoreSelect value={f.store_id} onChange={(v) => setF({ ...f, store_id: v })} stores={stores} testid="new-user-store-select" /></div>
              <div className="space-y-1.5"><Label>Weekly off</Label>
                <Select value={f.weekly_off} onValueChange={(v) => setF({ ...f, weekly_off: v })}>
                  <SelectTrigger className="h-11" data-testid="new-user-weekly-off-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          {f.role === "manager" && (
            <div className="space-y-2"><Label>Assigned stores</Label>
              <div className="grid grid-cols-2 gap-2">{stores.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm"><Checkbox checked={f.store_ids.includes(s.id)} data-testid={`new-user-store-check-${s.id}`}
                  onCheckedChange={(c) => setF({ ...f, store_ids: c ? [...f.store_ids, s.id] : f.store_ids.filter((x) => x !== s.id) })} />{s.name}</label>
              ))}</div>
            </div>
          )}
          <div className="space-y-1.5"><Label>Temporary password</Label><Input className="h-11" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-password-input" />
            <p className="text-xs text-slate-500">They'll be asked to change it on first login.</p></div>
          {err && <div className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="new-user-error">{err}</div>}
        </div>
        <DialogFooter><Button onClick={submit} className="bg-[#011E38] h-11" data-testid="new-user-submit">Create account</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionDialog({ state, setState, onDone }) {
  const { stores } = useAuth();
  const [val, setVal] = useState("");
  const [ids, setIds] = useState([]);
  const [ef, setEf] = useState({});
  useEffect(() => {
    setVal("");
    setIds(state?.user?.store_ids || []);
    const u = state?.user;
    if (u) setEf({ status: u.status === "active" || (u.status == null && u.active) ? "inactive" : "active", name: u.name, employee_id: u.employee_id, email: u.email || "", mobile: u.mobile || "", date_of_joining: u.date_of_joining || "", weekly_off: u.weekly_off == null ? "none" : String(u.weekly_off) });
  }, [state]);
  if (!state) return null;
  const { type, user: u } = state;
  const run = async () => {
    try {
      if (type === "reset") await api.post(`/users/${u.id}/reset-password`, { password: val });
      if (type === "transfer") await api.post(`/users/${u.id}/transfer`, { store_id: val });
      if (type === "stores") await api.put(`/users/${u.id}`, { store_ids: ids });
      if (type === "status") await api.post(`/users/${u.id}/status`, { status: ef.status, reason: val });
      if (type === "edit") {
        const body = { name: ef.name, employee_id: ef.employee_id, email: ef.email, mobile: ef.mobile, date_of_joining: ef.date_of_joining };
        if (u.role === "staff") ef.weekly_off === "none" ? (body.clear_weekly_off = true) : (body.weekly_off = Number(ef.weekly_off));
        await api.put(`/users/${u.id}`, body);
      }
      toast.success("Saved");
      setState(null);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={() => setState(null)}>
      <DialogContent>
        <DialogHeader><DialogTitle>{{ reset: "Reset password", transfer: "Transfer staff", stores: "Assign stores", edit: "Edit details", status: "Change status" }[type]} · {u.name}</DialogTitle></DialogHeader>
        {type === "reset" && <><Input placeholder="New temporary password" value={val} onChange={(e) => setVal(e.target.value)} data-testid="reset-password-input" /><p className="text-xs text-slate-500">They must change it at next login.</p></>}
        {type === "transfer" && <><StoreSelect value={val} onChange={setVal} stores={stores.filter((s) => s.id !== u.store_id)} testid="transfer-store-select" /><p className="text-xs text-slate-500">Past performance stays with the current store. New entries go to the new store.</p></>}
        {type === "status" && (
          <div className="grid gap-3">
            <Select value={ef.status} onValueChange={(v) => setEf({ ...ef, status: v })}>
              <SelectTrigger className="h-11" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Reason (required)" value={val} onChange={(e) => setVal(e.target.value)} data-testid="status-reason-input" />
            <p className="text-xs text-slate-500">Inactive, Transferred or Left staff cannot sign in or submit entries. Their past sales, KPIs and reports stay unchanged.</p>
          </div>
        )}
        {type === "stores" && <div className="grid grid-cols-2 gap-2">{stores.map((s) => (
          <label key={s.id} className="flex items-center gap-2 text-sm"><Checkbox checked={ids.includes(s.id)} onCheckedChange={(c) => setIds(c ? [...ids, s.id] : ids.filter((x) => x !== s.id))} data-testid={`assign-store-check-${s.id}`} />{s.name}</label>))}</div>}
        {type === "edit" && (
          <div className="grid gap-3">
            <div className="space-y-1.5"><Label>Employee name</Label><Input className="h-11" value={ef.name || ""} onChange={(e) => setEf({ ...ef, name: e.target.value })} data-testid="edit-user-name-input" /></div>
            <div className="space-y-1.5"><Label>Employee ID</Label><Input className="h-11" value={ef.employee_id || ""} onChange={(e) => setEf({ ...ef, employee_id: e.target.value })} data-testid="edit-user-employee-id-input" />
              <p className="text-xs text-slate-500">They will sign in with the new Employee ID. Past performance is kept.</p></div>
            <ProfileFields f={ef} setF={setEf} prefix="edit-user" />
            {u.role === "staff" && (
              <div className="space-y-1.5"><Label>Weekly off</Label>
                <Select value={ef.weekly_off} onValueChange={(v) => setEf({ ...ef, weekly_off: v })}>
                  <SelectTrigger className="h-11" data-testid="edit-user-weekly-off-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
        <DialogFooter><Button onClick={run} className="bg-[#011E38]" disabled={(type === "reset" || type === "transfer" || type === "status") && !val} data-testid="action-dialog-confirm">Confirm</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Team() {
  const { user, stores, storeId } = useAuth();
  const [rows, setRows] = useState(null);
  const [role, setRole] = useState("all");
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [del, setDel] = useState(null);
  const storeName = (id) => stores.find((s) => s.id === id)?.name || "—";

  const load = useCallback(() => {
    const params = {};
    if (storeId && storeId !== "all") params.store_id = storeId;
    if (role !== "all") params.role = role;
    api.get("/users", { params }).then((r) => setRows(r.data));
  }, [storeId, role]);
  useEffect(() => { load(); }, [load]);


  return (
    <div className="space-y-8">
      <PageHeader eyebrow={storeId === "all" ? "All stores" : storeName(storeId)} title="Team" testid="team-title"
        right={<Button onClick={() => setOpen(true)} className="h-12 rounded-full px-6 bg-[#011E38] hover:bg-[#032C52]" data-testid="add-user-button"><Plus className="w-4 h-4 mr-2" /> Add {user.role === "admin" ? "user" : "staff"}</Button>} />
      {user.role === "admin" && (
        <div className="flex gap-2" data-testid="role-filter">
          {["all", "staff", "manager", "admin"].map((r) => (
            <button key={r} onClick={() => setRole(r)} data-testid={`role-filter-${r}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border ${role === r ? "bg-[#011E38] text-white border-[#011E38]" : "bg-white border-slate-200 text-slate-600"}`}>{r === "all" ? "All" : ROLE_LABEL[r]}</button>
          ))}
        </div>
      )}
      {!rows ? <Loading /> : (
        <div className="bs-card overflow-x-auto">
          <table className="w-full text-left" data-testid="team-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr>{["Name", "Employee ID", "Contact", "Role", "Store", "Weekly off", "Status", ""].map((h) => <th key={h} className="px-5 py-4">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-b border-slate-50" data-testid={`team-row-${u.employee_id}`}>
                  <td className="px-5 py-4 font-semibold text-[#011E38]">{u.name}{u.must_change_password && <div className="text-xs text-orange-600 font-normal">Temp password</div>}</td>
                  <td className="px-5 num">{u.employee_id}</td>
                  <td className="px-5 text-xs text-slate-500">{u.email || "—"}<div className="num">{u.mobile || ""}</div></td>
                  <td className="px-5 text-sm">{ROLE_LABEL[u.role]}</td>
                  <td className="px-5 text-sm">{u.role === "staff" ? storeName(u.store_id) : u.role === "manager" ? (u.store_ids || []).map(storeName).join(", ") : "All"}</td>
                  <td className="px-5 text-sm">{u.weekly_off != null ? WEEKDAYS[u.weekly_off] : "—"}</td>
                  <td className="px-5">
                    {(() => { const s = u.status || (u.active ? "active" : "inactive"); return (
                      <button disabled={u.id === user.id} onClick={() => setAction({ type: "status", user: u })} data-testid={`status-${u.employee_id}`}
                        className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[s]}`}>{STATUS_LABEL[s]}</button>); })()}
                    {u.status_reason && u.status !== "active" && <div className="text-[0.65rem] text-slate-400 mt-1 max-w-[10rem] truncate" title={u.status_reason}>{u.status_reason}</div>}
                  </td>
                  <td className="px-5">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => setAction({ type: "edit", user: u })} data-testid={`edit-user-${u.employee_id}`}><Pencil className="w-4 h-4" /></Button>
                      {u.id !== user.id && <Button variant="ghost" size="sm" onClick={() => setAction({ type: "reset", user: u })} data-testid={`reset-password-${u.employee_id}`}><KeyRound className="w-4 h-4" /></Button>}
                      {u.role === "staff" && <Button variant="ghost" size="sm" onClick={() => setAction({ type: "transfer", user: u })} data-testid={`transfer-${u.employee_id}`}><ArrowRightLeft className="w-4 h-4" /></Button>}
                      {u.role === "manager" && user.role === "admin" && <Button variant="ghost" size="sm" onClick={() => setAction({ type: "stores", user: u })} data-testid={`assign-stores-${u.employee_id}`}><StoreIcon className="w-4 h-4" /></Button>}
                      {user.role === "admin" && u.id !== user.id && <Button variant="ghost" size="sm" onClick={() => setDel(u)} data-testid={`delete-user-${u.employee_id}`}><Trash2 className="w-4 h-4 text-red-700" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <Empty>No users found.</Empty>}
        </div>
      )}
      <CreateUser open={open} setOpen={setOpen} onDone={load} />
      <ActionDialog state={action} setState={setAction} onDone={load} />
      {del && <DeleteDialog title={`Delete ${del.name}`} word={del.employee_id} path={`/users/${del.id}`}
        warning="This permanently removes the account and all of their daily entries, returns and targets. Store totals will drop."
        onClose={() => setDel(null)} onDone={() => { setDel(null); load(); }} />}
      <p className="text-xs text-slate-400">Passwords are securely hashed and never visible. Reset issues a new temporary password.</p>
    </div>
  );
}
